/**
 * `/casos/:id/saidas` (E159.F3, GUS-153; ADR-0109 §2.7): "Conferir laudo e orientações", na área
 * da equipe. As saídas da mesma versão — o laudo e, em PARTE, as orientações —, cada uma com a
 * situação da conferência, o estado, os apontamentos (trecho, documento × fonte, fonte e onde
 * corrigir) e os critérios por expansão; o limite do catálogo; e "Conferir agora". A tela não
 * decide nada: tudo vem do servidor. O link de entrada sai do resultado, das orientações e do laudo.
 * E170.F2.C05: o "Conferir agora" só com a conferência liberada (`ConferirAgora`).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ConferirAgora, SaidaNaConferencia } from '@/components/ConferenciaDasSaidas';
import { ProximoPassoDoResultado } from '@/components/ProximoPasso/ProximoPasso';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  ConferenciaDasSaidasError,
  conferirAgora,
  lerAConferenciaDasSaidas,
  type ConferenciaDasSaidasData,
  type MotivoDaFalha,
} from '@/lib/conferenciaDasSaidas';
import { useResultadoDoCaso } from '@/lib/useResultadoDoCaso';

const t = ptBR.conferenciaDasSaidas;

function motivoDe(err: unknown): MotivoDaFalha {
  return err instanceof ConferenciaDasSaidasError ? err.motivo : 'indisponivel';
}

type Aviso = keyof typeof t.aviso;

const TOM_DO_AVISO: Record<Aviso, string> = {
  corrigir: 'border-danger-500 bg-danger-50 text-danger-700',
  conferir: 'border-warning-500 bg-warning-50 text-warning-700',
  pronto: 'border-border bg-surface text-fg',
};

/** O aviso do topo pela pendência que o servidor deu a cada saída. */
function aviso(dados: ConferenciaDasSaidasData): Aviso {
  const pendencias = dados.saidas.map((s) => s.pendencia);
  if (pendencias.includes('corrigir')) return 'corrigir';
  if (pendencias.includes('conferir')) return 'conferir';
  return 'pronto';
}

function SaidasDaVersao({ dados }: { dados: ConferenciaDasSaidasData }) {
  return (
    <section className="border border-border bg-surface p-sc-4" aria-labelledby="saidas-da-versao">
      <h2 id="saidas-da-versao" className="text-body-lg font-semibold text-fg">
        {t.saidasDaVersao}
      </h2>
      {dados.rodada !== null ? (
        <p className="text-caption text-fg-muted" data-testid="rodada-vigente">
          {t.rodadaVigente.replace('{rodada}', String(dados.rodada))}
        </p>
      ) : null}
      {dados.saidas.map((s) => (
        <SaidaNaConferencia key={s.kind} saida={s} caseId={dados.case_id} />
      ))}
    </section>
  );
}

function Limite({ limite }: { limite: string }) {
  return (
    <section className="max-w-prose" data-testid="limite-do-catalogo">
      <h2 className="text-body-sm font-semibold text-fg">{t.limiteTitulo}</h2>
      <p className="text-body-sm text-fg-muted">{limite}</p>
    </section>
  );
}

function Conteudo({ dados }: { dados: ConferenciaDasSaidasData }) {
  if (!dados.aplicavel) {
    return <p className="text-body-sm text-fg-muted">{t.naoAplicavel}</p>;
  }
  const tom = dados.motivo === 'sem_resultado' ? 'conferir' : aviso(dados);
  return (
    <>
      <div
        role="status"
        className={`border p-sc-4 text-body-sm ${TOM_DO_AVISO[tom]}`}
        data-testid="aviso-da-conferencia"
        data-tom={tom}
      >
        {dados.motivo === 'sem_resultado' ? t.semResultado : t.aviso[tom]}
      </div>
      <SaidasDaVersao dados={dados} />
      <Limite limite={dados.limite} />
    </>
  );
}

export function ConferenciaDasSaidasPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const [dados, setDados] = useState<ConferenciaDasSaidasData | null>(null);
  const [falha, setFalha] = useState<MotivoDaFalha | null>(null);
  const [conferindo, setConferindo] = useState(false);
  // Uma leitura do resultado para a faixa do próximo passo e para o botão (F-E162.F2-02).
  const { resultado } = useResultadoDoCaso(client, caseId, dados);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await lerAConferenciaDasSaidas(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(motivoDe(err));
    }
  }, [client, caseId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function conferir(): Promise<void> {
    setConferindo(true);
    try {
      setDados(await conferirAgora(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(motivoDe(err));
    } finally {
      setConferindo(false);
    }
  }

  return (
    <section
      className="mx-auto flex max-w-5xl flex-col gap-sc-6 p-sc-6"
      data-testid="conferencia-das-saidas-page"
    >
      <header>
        <p className="text-caption uppercase tracking-[0.1em] text-fg-muted">{t.sobretitulo}</p>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.subtitulo}</p>
        <nav className="mt-sc-2 flex flex-wrap gap-sc-4 text-body-sm">
          <Link className="text-accent-700" to={`/casos/${caseId}/resultado`}>
            {t.voltar}
          </Link>
          {/* E171.F3 (GUS-145): o apontamento de redação se corrige na mesa (E202.F3). */}
          <Link
            className="text-accent-700"
            to={`/casos/${caseId}/mesa`}
            data-testid="saidas-correcao"
          >
            {ptBR.editorManual.corrigirNaMesa}
          </Link>
        </nav>
      </header>
      <ProximoPassoDoResultado caseId={caseId} resultado={resultado} />
      {falha !== null ? (
        <div role="alert" className="space-y-sc-2" data-testid="conferencia-das-saidas-erro">
          <p className="text-body-sm text-danger-700">{t.erro[falha]}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void carregar()}>
            {t.tentarDeNovo}
          </Button>
        </div>
      ) : null}
      {dados === null && falha === null ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {dados !== null ? <Conteudo dados={dados} /> : null}
      {dados?.aplicavel ? (
        <ConferirAgora
          caseId={caseId}
          resultado={resultado}
          conferindo={conferindo}
          onConferir={() => void conferir()}
        />
      ) : null}
    </section>
  );
}
